// Single source of truth for the cost-guide directory.
// Used by the homepage bento grid (PopularProjects) and the /cost-guides
// index page. Keep in sync with TOPICAL_CLUSTERS in CostGuide.jsx.

// Bento-grid top 6 — flagship visual treatment for the most-trafficked queries.
export const FLAGSHIP_GUIDES = [
  { slug: "kitchen-renovation", title: "Kitchen Renovation", range: "£5,000 – £25,000", icon: "fas fa-utensils", image: "/images/projects/kitchen-renovation.jpg", wide: true },
  { slug: "bathroom-renovation", title: "Bathroom Renovation", range: "£3,000 – £15,000", icon: "fas fa-bath", image: "/images/projects/bathroom-renovation.jpg" },
  { slug: "loft-conversion", title: "Loft Conversion", range: "£20,000 – £65,000", icon: "fas fa-home", image: "/images/projects/loft-conversion.jpg" },
  { slug: "new-boiler", title: "New Boiler", range: "£1,800 – £5,000", icon: "fas fa-fire", image: "/images/projects/new-boiler.jpg", wide: true },
  { slug: "painting-decorating", title: "Painting & Decorating", range: "£500 – £5,000", icon: "fas fa-paint-roller", image: "/images/projects/painting-decorating.jpg" },
  { slug: "driveway", title: "New Driveway", range: "£2,000 – £12,000", icon: "fas fa-road", image: "/images/projects/driveway.jpg" },
];

// Full directory — grouped by topical cluster.
export const ALL_GUIDE_CLUSTERS = [
  {
    heading: "Kitchen & Interior",
    icon: "fas fa-utensils",
    items: [
      { slug: "kitchen-renovation", title: "Kitchen Renovation", range: "£5K – £25K" },
      { slug: "kitchen-extension", title: "Kitchen Extension", range: "£25K – £100K" },
      { slug: "kitchen-worktop", title: "Kitchen Worktops", range: "£600 – £4.5K" },
      { slug: "kitchen-island", title: "Kitchen Island", range: "£1.2K – £8K" },
      { slug: "utility-room", title: "Utility Room", range: "£2.5K – £15K" },
      { slug: "painting-decorating", title: "Painting & Decorating", range: "£500 – £5K" },
      { slug: "plastering", title: "Plastering", range: "£300 – £4K" },
      { slug: "flooring", title: "Flooring", range: "£500 – £6K" },
      { slug: "tiling", title: "Tiling", range: "£25 – £90/m²" },
    ],
  },
  {
    heading: "Bathroom",
    icon: "fas fa-bath",
    items: [
      { slug: "bathroom-renovation", title: "Bathroom Renovation", range: "£3K – £15K" },
      { slug: "new-bathroom-suite", title: "New Bathroom Suite", range: "£800 – £3K" },
      { slug: "bathroom-tiling", title: "Bathroom Tiling", range: "£300 – £2K" },
    ],
  },
  {
    heading: "Exterior & Garden",
    icon: "fas fa-seedling",
    items: [
      { slug: "driveway", title: "Driveway", range: "£2K – £12K" },
      { slug: "patio", title: "Patio", range: "£1K – £5K" },
      { slug: "garden-landscaping", title: "Garden Landscaping", range: "£1.5K – £15K" },
      { slug: "fencing", title: "Fencing", range: "£300 – £4K" },
      { slug: "artificial-grass", title: "Artificial Grass", range: "£60 – £85/m²" },
      { slug: "tree-surgery", title: "Tree Surgery", range: "£200 – £1.5K" },
      { slug: "decking", title: "Garden Decking", range: "£90 – £220/m²" },
      { slug: "garden-room", title: "Garden Room", range: "£6K – £30K" },
      { slug: "waste-removal", title: "Waste Removal", range: "£180 – £300" },
    ],
  },
  {
    heading: "Roof & Structural",
    icon: "fas fa-home",
    items: [
      { slug: "new-roof", title: "New Roof", range: "£5K – £15K" },
      { slug: "flat-roof", title: "Flat Roof", range: "£1.5K – £5K" },
      { slug: "roof-repair", title: "Roof Repair", range: "£150 – £1.5K" },
      { slug: "guttering", title: "Guttering & Fascias", range: "£400 – £1.5K" },
      { slug: "scaffolding", title: "Scaffolding Hire", range: "£500 – £2K/wk" },
      { slug: "rendering", title: "House Rendering", range: "£3K – £14K" },
      { slug: "damp-proofing", title: "Damp Proofing", range: "£500 – £4K" },
      { slug: "structural-wall", title: "Wall Removal & RSJ", range: "£1.8K – £7.5K" },
      { slug: "porch", title: "Porch", range: "£3.5K – £12K" },
      { slug: "asbestos", title: "Asbestos Removal", range: "£250 – £1.5K" },
      { slug: "drainage", title: "Surface Water Drainage", range: "£350 – £1.2K" },
    ],
  },
  {
    heading: "Energy & Heating",
    icon: "fas fa-fire",
    items: [
      { slug: "new-boiler", title: "New Boiler", range: "£1.8K – £5K" },
      { slug: "central-heating", title: "Central Heating", range: "£3K – £10K" },
      { slug: "heat-pump-installation", title: "Heat Pump", range: "£7K – £18K" },
      { slug: "solar-panel-installation", title: "Solar Panels", range: "£4K – £14K" },
      { slug: "ev-charger-installation", title: "EV Charger", range: "£800 – £1.5K" },
      { slug: "insulation", title: "Insulation", range: "£1K – £4K" },
      { slug: "underfloor-heating", title: "Underfloor Heating", range: "£3K – £10K" },
      { slug: "rewiring", title: "Rewiring", range: "£3K – £10K" },
    ],
  },
  {
    heading: "Glazing & Conversions",
    icon: "fas fa-border-all",
    items: [
      { slug: "double-glazing", title: "Double Glazing", range: "£3K – £12K" },
      { slug: "window-installation", title: "Window Installation", range: "£300/window" },
      { slug: "door-installation", title: "Door Installation", range: "£200 – £2K" },
      { slug: "loft-conversion", title: "Loft Conversion", range: "£20K – £65K" },
      { slug: "house-extension", title: "House Extension", range: "£25K – £100K" },
      { slug: "garage-conversion", title: "Garage Conversion", range: "£8K – £30K" },
      { slug: "conservatory", title: "Conservatory", range: "£8K – £30K" },
    ],
  },
  {
    heading: "Carpentry & Joinery",
    icon: "fas fa-hammer",
    items: [
      { slug: "carpentry", title: "Carpentry", range: "£200/day" },
      { slug: "internal-doors", title: "Internal Doors", range: "£110 – £350" },
      { slug: "skirting", title: "Skirting Boards", range: "£220 – £2.8K" },
      { slug: "coving", title: "Coving & Cornice", range: "£250 – £800" },
      { slug: "staircase", title: "New Staircase", range: "£1.5K – £6K" },
      { slug: "wardrobes", title: "Fitted Wardrobes", range: "£1.2K – £5K" },
      { slug: "loft-boarding", title: "Loft Boarding & Ladders", range: "£300 – £2.5K" },
    ],
  },
];

export const TOTAL_GUIDE_COUNT = ALL_GUIDE_CLUSTERS.reduce(
  (sum, cluster) => sum + cluster.items.length,
  0
);
